import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Cancel, PlaceholderImg, SearchIcon } from "../../../styled/AllImages";
import {
  Box,
  ButtonStyled,
  HeadingStyled,
  LazyImage,
  SpanStyled,
} from "../../../styled/Elements";

import { useTranslation } from "react-i18next";
import { CustomCheckbox } from "../../../styled/FormElements";
import styled from "styled-components";
import FloatingInput from "../../inputFields/FloatingInput";
import { useFormikContext } from "formik";

const MultipleUsersModal = ({
  show,
  onHide,
  title,
  mainParents,
  selectedMainParents,
  setSelectedMainParents,
}) => {
  const { t } = useTranslation();
  const ASSET_URL = process.env.REACT_APP_ASSET_URL;
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [filteredList, setFilteredList] = useState([]);

  const { values } = useFormikContext();

  // Filter logic
  const handleSearch = (e) => {
    const { value } = e.target;
    const searchQuery = value.toLowerCase();
    const updatedList = mainParents.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(searchQuery);
      const labelMatch = item.subLabel?.toLowerCase().includes(searchQuery);
      return nameMatch || labelMatch;
    });
    setFilteredList(updatedList);
  };

  useEffect(() => {
    setFilteredList(mainParents);
  }, [mainParents, show]);

  useEffect(() => {
    if (show) {
      setCheckedUsers(selectedMainParents || []);
    }
  }, [show, selectedMainParents]);

  // Main User Checkbox Toggle
  const handleCheckboxChange = (mainParent) => {
    if (checkedUsers.some((selected) => selected.id === mainParent.id)) {
      setCheckedUsers(checkedUsers.filter((selected) => selected.id !== mainParent.id));
    } else {
      // Add user with default referral properties
      setCheckedUsers([...checkedUsers, { ...mainParent, shareReferrals: false, referralNote: "", referralUniqueKey: `my_department_${mainParent.id}` }]);
    }
  };

  // Toggle "Share Referrals" for a specific user
  const handleToggleShare = (ukey) => {
    setCheckedUsers((prev) =>
      prev.map((user) =>
        user.referralUniqueKey === ukey ? { ...user, shareReferrals: !user.shareReferrals } : user
      )
    );
  };

  // Update Note for a specific user
  const handleNoteChange = (uKey, value) => {
    setCheckedUsers((prev) =>
      prev.map((user) =>
        user.referralUniqueKey === uKey ? { ...user, referralNote: value } : user
      )
    );
  };

  const handleAddClick = () => {
    setSelectedMainParents(checkedUsers);
    onHide();
  };

  return (
    <Modal size="md" show={show} onHide={onHide} centered>
      <Box className="d-flex justify-content-between align-items-center p-3">
        <HeadingStyled size="24px" color="var(--text)">
          {title}
        </HeadingStyled>
        <span className="close-button cursor-pointer" onClick={onHide}>
          <Cancel />
        </span>
      </Box>
      <Box className="d-flex p-3 pt-0 border-bottom">
        <FloatingInput
          name="search"
          type="input"
          fieldType="text"
          label={t("search")}
          placeholder={t("search")}
          icon={SearchIcon}
          onChange={handleSearch}
        />
      </Box>

      <ScrollableContainer>
        {filteredList?.map((mainParent) => {
          const uKey = `my_department_${mainParent.id}`;
          const isChecked = checkedUsers.some((u) => u.referralUniqueKey === uKey);
          const currentUserData = checkedUsers.find((u) => u.id === mainParent.id);

          return (
            <MultipleUsersRow
              className={`userDataRow ${isChecked ? "selected" : ""}`}
              key={mainParent.id}
            >
              <Box className="userDataContainer">
                <Box className="userDataInfo">
                  <Box className="userPlaceholder">
                    <LazyImage
                      src={
                        mainParent?.profile
                          ? `${ASSET_URL}/${mainParent?.profile}`
                          : PlaceholderImg
                      }
                      alt="User"
                    />
                  </Box>
                  <Box className="userDataNameRole">
                    <SpanStyled color="var(--text)" size="16px" weight="bold">
                      {mainParent.name}
                    </SpanStyled>
                    {mainParent?.subLabel && (
                      <SpanStyled color="var(--text2)" size="14px">
                        {mainParent?.subLabel}
                      </SpanStyled>
                    )}
                  </Box>
                  <Box className="userDataCheckBox">
                    <CustomCheckbox
                      name="users"
                      checked={isChecked}
                      onChange={() => handleCheckboxChange(mainParent)}
                    />
                  </Box>
                </Box>

                {/* Conditional Referrals Section */}
                {isChecked && (
                  <Box className="w-100 p-2 border-top mt-1" style={{ background: 'var(--bgSecondary)', borderRadius: '8px' }}>
                    <Box className="d-flex align-items-center gap-2 mb-2">
                      <CustomCheckbox
                        name={`share_${mainParent.id}`}
                        checked={currentUserData?.shareReferrals || false}
                        onChange={() => handleToggleShare(uKey)}
                      />
                      <SpanStyled color="var(--text)" size="14px">
                        {t("Do you want to share referrals with this user?")}
                      </SpanStyled>
                    </Box>

                    {currentUserData?.shareReferrals && (
                      <Box className="mt-2">
                        <textarea
                          className="form-control"
                          placeholder={t("Enter referral details...")}
                          rows={2}
                          style={{ fontSize: '14px', borderRadius: '6px' }}
                          value={currentUserData?.referralNote || ""}
                          onChange={(e) => handleNoteChange(uKey, e.target.value)}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </MultipleUsersRow>
          );
        })}
      </ScrollableContainer>

      <div className="modal-footer">
        <ButtonStyled
          label={t("Cancel")}
          onClick={onHide}
          bg="var(--bgSecondary)"
          color="var(--text2)"
        />
        <ButtonStyled
          bg="var(--themeColor)"
          label={t("add")}
          onClick={handleAddClick}
        />
      </div>
    </Modal>
  );
};

export default MultipleUsersModal;

// --- Styled Components ---

const MultipleUsersRow = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--borderLight);
  width: 100%;
  
  .userDataContainer {
    display: flex;
    width: 100%;
    align-items: start;
    justify-content: space-between;
    flex-direction: column;
  }
  .userDataInfo {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 16px;
    .userPlaceholder {
      min-width: 48px;
      max-width: 48px;
      height: 48px;
      border-radius: 50%;
      overflow: hidden;
      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }
    .userDataNameRole {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
`;

const ScrollableContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: var(--bgSecondary); }
  &::-webkit-scrollbar-thumb { background: #e4e8ef; border-radius: 10px; }
`;